import { useEffect, useState } from "react";
import { apiPost, apiPatch } from "../../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../ui/select";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

const MASK = "••••••••";

export default function ConnectorDialog({ open, onOpenChange, spec, instance, onSaved }) {
  const editing = !!instance;
  const [config, setConfig] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open || !spec) return;
    setResult(null); setErr("");
    const init = {};
    spec.fields.forEach((f) => { init[f.name] = instance?.config?.[f.name] ?? ""; });
    setConfig(init);
  }, [open, spec, instance]);

  if (!spec) return null;
  const set = (k, v) => setConfig((c) => ({ ...c, [k]: v }));

  const test = async () => {
    setBusy(true); setResult(null); setErr("");
    try {
      const res = editing
        ? await apiPost(`/connectors/instances/${instance.id}/test`)
        : await apiPost("/connectors/test", { connector_key: spec.key, config });
      setResult(res);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Test failed.");
    } finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true); setErr("");
    try {
      if (editing) await apiPatch(`/connectors/instances/${instance.id}`, { config });
      else await apiPost("/connectors/instances", { connector_key: spec.key, config });
      onSaved();
      onOpenChange(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save connector.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[560px] max-h-[90vh] overflow-y-auto" data-testid="connector-dialog">
        <DialogHeader>
          <DialogTitle className="small-caps flex items-center gap-2">
            {spec.name}
            {spec.verified && <span className="text-[10px] font-bold" style={{ background: "rgba(62,124,79,.12)", color: "#2F6B41", borderRadius: 999, padding: "2px 8px" }}>Verified</span>}
          </DialogTitle>
        </DialogHeader>
        <p className="text-[13px] mb-1" style={{ color: "var(--cv-dark-grey)" }}>{spec.description}</p>
        {spec.docs_url && (
          <a href={spec.docs_url} target="_blank" rel="noreferrer" className="text-[12px] font-bold" style={{ color: "var(--cv-crimson)" }}>API documentation →</a>
        )}

        <div className="space-y-3 mt-3">
          {spec.fields.map((f) => (
            <div key={f.name} className="space-y-1.5">
              <Label>{f.label}{f.required && <span style={{ color: "var(--cv-crimson)" }}> *</span>}</Label>
              {f.type === "select" ? (
                <Select value={config[f.name] || ""} onValueChange={(v) => set(f.name, v)}>
                  <SelectTrigger data-testid={`cf-${f.name}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {(f.options || []).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  type={f.secret ? "password" : "text"}
                  value={config[f.name] || ""}
                  placeholder={f.secret && editing ? MASK : f.placeholder}
                  onChange={(e) => set(f.name, e.target.value)}
                  data-testid={`cf-${f.name}`}
                  autoComplete="off"
                />
              )}
            </div>
          ))}
          {editing && (
            <p className="text-[11.5px]" style={{ color: "var(--cv-dark-grey)" }}>
              Leave secret fields blank to keep the stored value. Test uses the saved credentials.
            </p>
          )}

          {result && (
            <div className="flex items-center gap-2 p-2.5 rounded text-[13px]" data-testid="connector-test-result"
              style={{ background: result.ok ? "rgba(62,124,79,.10)" : "rgba(165,48,15,.08)", color: result.ok ? "#2F6B41" : "#A5300F" }}>
              {result.ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
              <span>{result.message}{result.status ? ` (HTTP ${result.status})` : ""}</span>
            </div>
          )}
          {err && <div className="text-[13px]" style={{ color: "var(--cv-crimson)" }} data-testid="connector-error">{err}</div>}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={test} disabled={busy} data-testid="connector-test-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Test connection"}
          </Button>
          <Button onClick={save} disabled={busy} data-testid="connector-save"
            style={{ background: "var(--cv-crimson)", color: "#fff" }}>
            {editing ? "Save changes" : "Connect"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
